import type { ExportNotesSection } from '../schemas/export-notes.js';
import type { ExportAppearanceOptions } from '../schemas/export.js';
import { estimateWrappedLineCount } from './estimate-row-heights.js';
import {
  EXCEL_BORDER,
  EXCEL_FONT_SIZE_PT,
  EXCEL_LABEL_FILL_HEX,
  EXCEL_MAX_ROW_HEIGHT_PT,
  EXCEL_PT_PER_PX,
  pxToExcelWidthChars,
} from './excel-sheet-layout.js';
import { CSS_PX_PER_PT, DENSITY_PADDING_PX, LINE_HEIGHT_FACTOR } from './page-geometry.js';

/**
 * The « Notes » sheet of an Excel export (issue #19), described once as plain
 * data like `ExcelSheetLayout`: the backend's exceljs writer and the export
 * page's preview both consume it, so the preview cannot describe a different
 * sheet from the file.
 *
 * One row per message: the memo first, then each open Client discussion's note
 * and its replies, oldest first. A reply repeats its discussion's target (the
 * sheet stays filterable) and names the author it answers in « Réponse à ».
 */

export const EXCEL_NOTES_SHEET_NAME = 'Notes';

/** The columns, left to right: their header and width. */
export const EXCEL_NOTES_COLUMNS = [
  { label: 'Cible', widthPx: 220 },
  { label: 'Auteur', widthPx: 120 },
  { label: 'Date', widthPx: 110 },
  { label: 'Note', widthPx: 520 },
  { label: 'Réponse à', widthPx: 120 },
] as const;

const MEMO_TARGET = 'Mémo';
/** Same cell margin the matrix sheet's `contenu` estimate takes. */
const PADDING_PX = DENSITY_PADDING_PX.compact;

export interface ExcelNotesSheetColumn {
  label: string;
  widthPx: number;
  widthChars: number;
}

export interface ExcelNotesSheetRow {
  kind: 'memo' | 'note' | 'reply';
  /** One entry per column of `EXCEL_NOTES_COLUMNS`. */
  cells: string[];
  heightPt: number;
}

export interface ExcelNotesSheetLayout {
  name: typeof EXCEL_NOTES_SHEET_NAME;
  columns: ExcelNotesSheetColumn[];
  rows: ExcelNotesSheetRow[];
  headerHeightPt: number;
  frozen: { xSplit: 0; ySplit: 1 };
  border: { style: 'thin' | 'medium'; hex: string };
  headerFillHex: string;
}

function pxToPt(px: number): number {
  return Math.round(px * EXCEL_PT_PER_PX * 100) / 100;
}

/**
 * The height the row's cells need at their column widths - Excel never grows a
 * row that carries an explicit height, so a long note would be clipped
 * otherwise.
 */
function rowHeightPt(cells: readonly string[]): number {
  const fontPx = EXCEL_FONT_SIZE_PT * CSS_PX_PER_PT;
  const lines = Math.max(
    1,
    ...cells.map((cell, index) =>
      cell
        .split('\n')
        .reduce(
          (total, paragraph) =>
            total +
            estimateWrappedLineCount(
              paragraph,
              false,
              EXCEL_NOTES_COLUMNS[index]?.widthPx ?? 0,
              PADDING_PX,
              fontPx,
            ),
          0,
        ),
    ),
  );
  const heightPx = lines * Math.ceil(fontPx * LINE_HEIGHT_FACTOR) + 2 * PADDING_PX;
  return Math.min(EXCEL_MAX_ROW_HEIGHT_PT, pxToPt(heightPx));
}

export function buildExcelNotesSheetLayout(input: {
  section: ExportNotesSection;
  appearance: Pick<ExportAppearanceOptions, 'borders'>;
}): ExcelNotesSheetLayout {
  const rows: ExcelNotesSheetRow[] = [];
  const addRow = (kind: ExcelNotesSheetRow['kind'], cells: string[]): void => {
    rows.push({ kind, cells, heightPt: rowHeightPt(cells) });
  };

  if (input.section.memo !== null) {
    addRow('memo', [MEMO_TARGET, '', '', input.section.memo, '']);
  }
  for (const discussion of input.section.discussions) {
    const { message } = discussion;
    addRow('note', [discussion.target, message.author, message.date, message.text, '']);
    for (const reply of discussion.replies) {
      addRow('reply', [discussion.target, reply.author, reply.date, reply.text, message.author]);
    }
  }

  return {
    name: EXCEL_NOTES_SHEET_NAME,
    columns: EXCEL_NOTES_COLUMNS.map((column) => ({
      label: column.label,
      widthPx: column.widthPx,
      widthChars: pxToExcelWidthChars(column.widthPx),
    })),
    rows,
    headerHeightPt: pxToPt(
      Math.ceil(EXCEL_FONT_SIZE_PT * CSS_PX_PER_PT * LINE_HEIGHT_FACTOR) + 2 * PADDING_PX,
    ),
    frozen: { xSplit: 0, ySplit: 1 },
    border: { ...EXCEL_BORDER[input.appearance.borders] },
    headerFillHex: EXCEL_LABEL_FILL_HEX,
  };
}
